import { initializeChakraComponent } from 'Shared/chakra-initialize';
import {
   Flex,
   Button,
   useBreakpoint,
   Popover,
   PopoverContent,
   PopoverArrow,
   PopoverBody,
   HStack,
   Grid,
} from '@chakra-ui/react';
import { faNotdef } from '@fortawesome/pro-regular-svg-icons/faNotdef';
import { faWrench } from '@fortawesome/pro-regular-svg-icons/faWrench';
import { faHospital } from '@fortawesome/pro-regular-svg-icons/faHospital';
import { faPenLine } from '@fortawesome/pro-regular-svg-icons/faPenLine';
import { faUser } from '@fortawesome/pro-regular-svg-icons/faUser';
import { faCircleStar } from '@fortawesome/pro-regular-svg-icons/faCircleStar';
import { faBadgeCheck } from '@fortawesome/pro-solid-svg-icons/faBadgeCheck';
import { faGraduationCap } from '@fortawesome/pro-regular-svg-icons/faGraduationCap';
import { faOctagonExclamation } from '@fortawesome/pro-regular-svg-icons/faOctagonExclamation';
import { faImages } from '@fortawesome/pro-regular-svg-icons/faImages';
import { faHighlighterLine } from '@fortawesome/pro-regular-svg-icons/faHighlighterLine';
import { faCamera } from '@fortawesome/pro-regular-svg-icons/faCamera';
import { faCode } from '@fortawesome/pro-regular-svg-icons/faCode';
import { faListUl } from '@fortawesome/pro-regular-svg-icons/faListUl';
import { faScissors } from '@fortawesome/pro-regular-svg-icons/faScissors';
import { faFileCircleQuestion } from '@fortawesome/pro-regular-svg-icons/faFileCircleQuestion';
import { faFileCircleXmark } from '@fortawesome/pro-regular-svg-icons/faFileCircleXmark';
import { faImageSlash } from '@fortawesome/pro-regular-svg-icons/faImageSlash';
import { faFaceClouds } from '@fortawesome/pro-regular-svg-icons/faFaceClouds';
import { faHammerCrash } from '@fortawesome/pro-regular-svg-icons/faHammerCrash';
import { faCameraViewfinder } from '@fortawesome/pro-regular-svg-icons/faCameraViewfinder';
import { faFiles } from '@fortawesome/pro-regular-svg-icons/faFiles';
import { faTrashClock } from '@fortawesome/pro-regular-svg-icons/faTrashClock';
import { faPenField } from '@fortawesome/pro-regular-svg-icons/faPenField';
import { faSplit } from '@fortawesome/pro-regular-svg-icons/faSplit';
import { faMountain } from '@fortawesome/pro-regular-svg-icons/faMountain';
import { faBoxArchive } from '@fortawesome/pro-regular-svg-icons/faBoxArchive';
import { faRoute } from '@fortawesome/pro-regular-svg-icons/faRoute';
import { faFileCircleExclamation } from '@fortawesome/pro-regular-svg-icons/faFileCircleExclamation';
import { faMessageCaptions } from '@fortawesome/pro-regular-svg-icons/faMessageCaptions';
import { faHandshake } from '@fortawesome/pro-regular-svg-icons/faHandshake';
import { faRobot } from '@fortawesome/pro-solid-svg-icons/faRobot';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FlagBadge, FlagBadgeStyle } from './FlagBadge';
import { _js } from '@ifixit/localize';
import { Dispatch, useMemo, useState } from 'react';
import { FlexScrollGradient } from './FlexScrollGradient';
import { useHiddenWrap } from '@ifixit/carpenter-breadcrumbs/FlexHiddenWrap';
import { GuideData } from './GuideHeader';
import { useHistoryState } from './HistoryState';
import { HoverPopoverTrigger } from './HoverPopoverTrigger';
import { DifficultyBadge } from './DifficultyBadge';
import { TimeTakenBadge } from './TimeTakenBadge';

export const FAIconMapping: {
   [key: string]: IconDefinition;
} = {
   wrench: faWrench,
   hospital: faHospital,
   'pen-line': faPenLine,
   user: faUser,
   'circle-star': faCircleStar,
   'badge-check': faBadgeCheck,
   'graduation-cap': faGraduationCap,
   'octagon-exclamation': faOctagonExclamation,
   images: faImages,
   'highlighter-line': faHighlighterLine,
   camera: faCamera,
   code: faCode,
   'list-ul': faListUl,
   scissors: faScissors,
   'file-circle-question': faFileCircleQuestion,
   'file-circle-xmark': faFileCircleXmark,
   'image-slash': faImageSlash,
   'face-clouds': faFaceClouds,
   'hammer-crash': faHammerCrash,
   'camera-viewfinder': faCameraViewfinder,
   files: faFiles,
   'trash-clock': faTrashClock,
   'pen-field': faPenField,
   split: faSplit,
   mountain: faMountain,
   'box-archive': faBoxArchive,
   route: faRoute,
   'file-circle-exclamation': faFileCircleExclamation,
   'message-captions': faMessageCaptions,
   handshake: faHandshake,
   robot: faRobot,
};

const FlagBadgeStyleMapping: {
   [key: string]: FlagBadgeStyle;
} = {
   GUIDE_STARRED: { themeColor: 'blue', background: 'brand.100' },
   GUIDE_OEM: { themeColor: 'green' },
   MANUFACTURER_PROVIDED: { themeColor: 'green' },
};

const FlagBadgePriorityMapping: {
   [key: string]: number;
} = {
   GUIDE_STARRED: 3,
   GUIDE_OEM: 2,
   MANUFACTURER_PROVIDED: 1,
};

export const DefaultFlagStyle: FlagBadgeStyle = {
   themeColor: 'amber',
   background: 'white',
};

const MoreButtonReservedBuffer = 100;
const MoreButtonGap = 10;
const ItemGap = 6;

export type FlagSectionProps = Pick<
   GuideData,
   'difficulty' | 'difficultyName' | 'timeRequired' | 'flags' | 'isTeardown'
>;
export function FlagSection({
   difficulty,
   timeRequired,
   flags,
   isTeardown,
   difficultyName,
}: FlagSectionProps) {
   const breakpoint = useBreakpoint({ ssr: false });
   const [isExpanded, setIsExpanded] = useHistoryState(false, 'flagSectionExpanded');
   const isMobile = breakpoint == 'base';
   const nonFullWidthFlags = flags.filter(flag => !flag.fullWidth);

   const hasContent = !isTeardown || flags.length > 0;

   if (!hasContent) {
      return null;
   }

   if (isMobile) {
      return (
         <FlexScrollGradient overflowX="hidden" paddingTop="12px" paddingBottom="16px">
            <HStack
               spacing={`${ItemGap}px`}
               className="flags-list"
               justifyContent={isTeardown ? { base: 'normal', sm: 'center' } : 'normal'}
            >
               <StaticFlagSection
                  difficultyName={difficultyName}
                  isTeardown={isTeardown}
                  difficulty={difficulty}
                  timeRequired={timeRequired}
                  flags={nonFullWidthFlags}
               />
            </HStack>
         </FlexScrollGradient>
      );
   }

   return (
      <DynamicFlagSection
         difficultyName={difficultyName}
         difficulty={difficulty}
         timeRequired={timeRequired}
         flags={nonFullWidthFlags}
         isTeardown={isTeardown}
         isExpanded={isExpanded}
         setIsExpanded={setIsExpanded}
      />
   );
}

function DynamicFlagSection({
   difficulty,
   difficultyName,
   timeRequired,
   flags,
   isExpanded,
   isTeardown,
   setIsExpanded,
}: FlagSectionProps & {
   isExpanded: boolean;
   setIsExpanded: Dispatch<boolean>;
}) {
   const [hiddenFlagCount, setHiddenFlagCount] = useState<number>(0);
   const [left, setLeft] = useState<number>();

   const onDesktopFlagWrap = useMemo(
      () => (children: HTMLElement[]) => setHiddenFlagCount(() => children.length),
      []
   );

   const onDesktopFlagShown = (children: HTMLElement[], container: HTMLElement) => {
      const childrenWidthsMinusGap = children
         .map(child => child.offsetWidth)
         .reduce((a, b) => a + b, 0);
      const childrenWidth = ItemGap * (children.length - 1) + childrenWidthsMinusGap;
      let offset = container.offsetWidth - childrenWidth;
      if (isTeardown) {
         offset = offset / 2;
      }
      setLeft(-offset + MoreButtonGap);
   };

   const showLink = !isExpanded && hiddenFlagCount > 0;

   const hiddenWrapProps = useHiddenWrap<HTMLDivElement>({
      wrappedChildrenEffect: onDesktopFlagWrap,
      shownChildrenEffect: onDesktopFlagShown,
   });

   const flagSection = (
      <StaticFlagSection
         difficulty={difficulty}
         difficultyName={difficultyName}
         timeRequired={timeRequired}
         flags={flags}
         isTeardown={isTeardown}
      />
   );

   const justifyContent = isTeardown ? { base: 'normal', sm: 'center' } : 'normal';

   return (
      <Flex
         paddingTop="12px"
         paddingBottom="16px"
         alignItems="center"
         justifyContent={justifyContent}
      >
         {isExpanded ? (
            <Grid
               {...hiddenWrapProps}
               gap={`${ItemGap}px`}
               maxWidth="100%"
               className="flags-list"
               justifyContent={justifyContent}
            >
               {flagSection}
            </Grid>
         ) : (
            <HStack
               {...hiddenWrapProps}
               spacing={`${ItemGap}px`}
               maxWidth={`calc(100% - ${MoreButtonReservedBuffer}px)`}
               height={{ base: '28px', sm: '30px' }}
               overflow="hidden"
               className="flags-list"
               justifyContent={justifyContent}
            >
               {flagSection}
            </HStack>
         )}
         {showLink && (
            <Button
               variant="link"
               position="relative"
               left={`${left}px`}
               fontSize="14px"
               color={isTeardown ? 'white' : 'gray.700'}
               _visited={{ color: 'gray.700' }}
               _hover={{ textDecoration: 'none', color: 'gray.900' }}
               onClick={() => setIsExpanded(!isExpanded)}
               tabIndex={0}
            >
               {_js('+%1 more', hiddenFlagCount.toString())}
            </Button>
         )}
      </Flex>
   );
}

function StaticFlagSection({
   difficulty,
   difficultyName,
   timeRequired,
   flags,
   isTeardown,
}: FlagSectionProps) {
   const sortedFlags = flags.sort(
      (a, b) =>
         (FlagBadgePriorityMapping[b.flagid] || -1) - (FlagBadgePriorityMapping[a.flagid] || -1)
   );
   const Flags = sortedFlags.map(flag => {
      const badgeStyles = FlagBadgeStyleMapping[flag.flagid] || DefaultFlagStyle;
      const badgeProps = 'image' in flag ? { imageSrc: flag.image } : { icon: getIcon(flag.icon) };
      return (
         <Flex key={flag.title} className="flag">
            <Popover trigger="hover" strategy="fixed">
               <HoverPopoverTrigger>
                  <FlagBadge {...badgeStyles} {...badgeProps} className="flagTitle">
                     {flag.title}
                  </FlagBadge>
               </HoverPopoverTrigger>
               <PopoverContent>
                  <PopoverArrow backgroundColor="gray.800" />
                  <PopoverBody
                     borderRadius="md"
                     backgroundColor="gray.800"
                     color="white"
                     fontSize="14px"
                     className="flagText"
                  >
                     {flag.text}
                  </PopoverBody>
               </PopoverContent>
            </Popover>
         </Flex>
      );
   });

   if (isTeardown) {
      return <>{Flags}</>;
   }

   return (
      <>
         <Flex>
            <TimeTakenBadge timeRequired={timeRequired} className="guide-time-required" />
         </Flex>
         <Flex>
            <DifficultyBadge
               difficulty={difficulty}
               difficultyName={difficultyName}
               className={`guide-difficulty guide-difficulty-${difficulty}`}
            />
         </Flex>
         {Flags}
      </>
   );
}

export function getIcon(iconName: string): IconDefinition {
   return FAIconMapping[iconName] || faNotdef;
}

initializeChakraComponent('FlagSectionComponent', FlagSection);
